import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Send, BellRing, Smartphone, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  initGravitec,
  isPushSupported,
  getPushPermissionStatus,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
  getPushStatus,
} from '@/lib/gravitecPush';

export default function PushNotificationSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [status, setStatus] = useState({
    supported: false,
    permission: 'default',
    configured: false,
    subscriber_registered: false,
  });

  // Initialize Gravitec and load status
  useEffect(() => {
    initGravitec();
    loadStatus();
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const pushStatus = await getPushStatus();
      setStatus(pushStatus);
    } catch (err) {
      console.error('Failed to load push status:', err);
    }
    setLoading(false);
  };

  const handleSubscribe = async () => {
    if (!user?.email) {
      toast.error('Please log in to enable push notifications');
      return;
    }

    setSubscribing(true);
    try {
      const result = await subscribeToPush(user.email);
      if (result.success) {
        toast.success('Push notifications enabled! You\'ll receive alerts even when the app is closed.');
        await loadStatus();
      } else {
        toast.error(result.error || 'Failed to enable push notifications');
      }
    } catch (err) {
      toast.error('Failed to enable push notifications');
    }
    setSubscribing(false);
  };

  const handleUnsubscribe = async () => {
    setSubscribing(true);
    try {
      await unsubscribeFromPush();
      toast.info('Push notifications disabled');
      await loadStatus();
    } catch (err) {
      toast.error('Failed to disable push notifications');
    }
    setSubscribing(false);
  };

  const handleTestPush = async () => {
    setTestSending(true);
    try {
      const result = await sendTestPush();
      if (result.success) {
        toast.success('Test notification sent! Check your device.');
      } else {
        toast.error(result.error || 'Failed to send test notification');
      }
    } catch (err) {
      toast.error('Failed to send test notification');
    }
    setTestSending(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!status.supported) {
    return (
      <div className="flex items-center gap-2 p-3 bg-secondary/30 rounded-md border border-border">
        <X className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Push notifications not supported in this browser</span>
      </div>
    );
  }

  const isSubscribed = status.subscriber_registered && status.permission === 'granted';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Send className="w-5 h-5 text-accent-amber" />
        <h3 className="text-sm font-semibold">Push Notifications</h3>
      </div>
      
      <p className="text-xs text-muted-foreground leading-relaxed">
        Receive push notifications for reminders and backup alerts, <strong>even when the browser is closed</strong>. 
        These work like mobile app notifications.
      </p>

      <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-md border border-border">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <BellRing className="w-4 h-4 text-green-500" />
          ) : (
            <Smartphone className="w-4 h-4 text-muted-foreground" />
          )}
          <Label className="text-sm">Push Notifications</Label>
        </div>
        
        <div className="flex items-center gap-2">
          {status.permission === 'denied' ? (
            <span className="text-xs text-red-400">Blocked</span>
          ) : isSubscribed ? (
            <span className="text-xs text-green-500 flex items-center gap-1">
              <Check className="w-3 h-3" /> Active
            </span>
          ) : null}
          
          <Switch
            checked={isSubscribed}
            onCheckedChange={(checked) => checked ? handleSubscribe() : handleUnsubscribe()}
            disabled={subscribing || status.permission === 'denied'}
            data-testid="push-notification-toggle"
          />
        </div>
      </div>

      {status.permission === 'denied' && (
        <div className="text-xs text-muted-foreground p-2 bg-red-500/10 border border-red-500/20 rounded-md">
          Push notifications are blocked. To enable, click the lock icon in your browser's address bar and allow notifications for this site.
        </div>
      )}

      {!isSubscribed && status.permission !== 'denied' && (
        <Button 
          onClick={handleSubscribe} 
          size="sm" 
          variant="outline"
          className="w-full"
          disabled={subscribing}
          data-testid="enable-push-btn"
        >
          {subscribing ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5 mr-1.5" />
          )}
          Enable Push Notifications
        </Button>
      )}

      {isSubscribed && (
        <Button 
          onClick={handleTestPush}
          size="sm"
          variant="ghost"
          className="w-full text-muted-foreground"
          disabled={testSending}
          data-testid="test-push-btn"
        >
          {testSending ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <BellRing className="w-3.5 h-3.5 mr-1.5" />
          )}
          Send Test Push Notification
        </Button>
      )}

      {isSubscribed && (
        <div className="text-[10px] text-muted-foreground/70 space-y-1">
          <p>✓ Reminder notifications when notes are due</p>
          <p>✓ Backup reminders when it's time to backup</p>
          <p>✓ Works even when VaultNotes is closed</p>
        </div>
      )}
    </div>
  );
}
